import { useEffect, useRef } from "react";

/**
 * A small segmented control: every option visible at once.
 *
 * Built for the visibility picker, where a dropdown hides the choice
 * behind a click on the one control a coach most needs to trust —
 * mis-sharing is the scariest failure mode in youth coaching, and a
 * closed `<select>` shows one label while the other two stay invisible.
 * Laying the ladder out flat means the audience is readable without
 * interacting.
 *
 * Deliberately a carve-out from the design system's "visible selects use
 * `TkticSelect`" rule: that rule exists to keep native chrome off app
 * surfaces, and this renders no native chrome either. It is for SHORT,
 * closed ladders (2–3 options) with a persistent selected state. A long
 * list, a free-form value, or a *command* menu (pick-then-apply, no
 * standing selection) still wants `TkticSelect` — see `BulkSelectionBar`,
 * which stays a dropdown for exactly that reason.
 *
 * ## The hidden mirror `<select>`
 *
 * The bare `data-testid` sits on a visually-clipped `<select>`, exactly
 * as `TkticSelect` does. That is not a testing hack bolted on after the
 * fact: it is the same compatibility bridge the native-select →
 * `TkticSelect` migration already used, so Playwright's `selectOption()`
 * and `toHaveValue()` keep working on the testid they always used, and
 * the value still serializes inside an uncontrolled `<form>`. The
 * visible buttons carry `data-value` so specs can assert per-segment
 * state the way the settings chip group's specs already do.
 *
 * `flex-wrap` is deliberate: in a narrow container (the goal composer's
 * "Delivery & tracking" rail is ~99px) the segments stack into a compact
 * radio list instead of overflowing. Both options stay visible, which is
 * the point — a wrapped control reads fine, a clipped one does not.
 *
 * Keyboard behaviour follows `ToneChipGroup` (the ARIA radio pattern):
 * one tab stop via roving `tabIndex`, arrows/Home/End to move, and
 * moving selects. `SettingChipGroup` predates that and leaves every chip
 * tabbable — this is the correct version.
 */

export type SegmentedOption<T extends string> = {
  value: T;
  label: string;
  disabled?: boolean;
};

export type SegmentedControlProps<T extends string> = {
  value: T | "";
  onChange: (next: T) => void;
  options: ReadonlyArray<SegmentedOption<T>>;
  /** Lands on the hidden mirror `<select>`, matching `TkticSelect`. */
  "data-testid"?: string;
  ariaLabel: string;
  name?: string;
  disabled?: boolean;
};

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  "data-testid": dataTestId,
  ariaLabel,
  name,
  disabled = false,
}: SegmentedControlProps<T>) {
  const ref = useRef<HTMLDivElement | null>(null);

  const selectable = options.filter((o) => !o.disabled);
  const activeIndex = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  );

  // Follow focus to the newly-active segment after an arrow move, but
  // only when the group already owns focus — otherwise an external
  // value change (a settings default seeding itself in) would steal it.
  useEffect(() => {
    if (!ref.current) return;
    if (!ref.current.contains(document.activeElement)) return;
    ref.current
      .querySelector<HTMLButtonElement>(`[data-segment-index="${activeIndex}"]`)
      ?.focus();
  }, [activeIndex]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const keys = [
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ];
    if (!keys.includes(e.key) || disabled) return;
    // Arrows move within the SELECTABLE set: stepping onto a disabled
    // segment would strand the roving tabindex on something unclickable.
    if (selectable.length === 0) return;
    e.preventDefault();

    const here = Math.max(
      0,
      selectable.findIndex((o) => o.value === value),
    );
    let next = here;
    if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      next = (here - 1 + selectable.length) % selectable.length;
    } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      next = (here + 1) % selectable.length;
    } else if (e.key === "Home") {
      next = 0;
    } else if (e.key === "End") {
      next = selectable.length - 1;
    }
    onChange(selectable[next].value);
  };

  return (
    <>
      {/* Same bridge `TkticSelect` uses: the bare testid stays on a
       * native select so existing `selectOption()` / `toHaveValue()`
       * assertions keep working, and the value serializes in a form. */}
      <select
        aria-hidden="true"
        tabIndex={-1}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        disabled={disabled}
        data-testid={dataTestId}
        style={{
          position: "absolute",
          opacity: 0,
          pointerEvents: "none",
          width: 1,
          height: 1,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
        }}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </select>

      <div
        ref={ref}
        role="radiogroup"
        aria-label={ariaLabel}
        onKeyDown={onKeyDown}
        data-testid={dataTestId ? `${dataTestId}-segments` : undefined}
        className="flex flex-wrap gap-1"
      >
        {options.map((opt, i) => {
          const active = opt.value === value;
          const isDisabled = disabled || !!opt.disabled;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={isDisabled}
              data-value={opt.value}
              data-segment-index={i}
              data-active={active}
              tabIndex={active ? 0 : -1}
              onClick={() => onChange(opt.value)}
              className={[
                "rounded-control px-2 py-1 text-xs font-display transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                "focus-visible:ring-offset-2 focus-visible:ring-offset-canvas",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                active
                  ? "bg-accent text-accent-ink"
                  : "border border-rule bg-surface text-ink-muted hover:bg-field hover:text-ink",
              ].join(" ")}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </>
  );
}
